import React from "react";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { GrainDefs, GrainOverlay } from "./Grain";
import { useReducedMotion } from "../motion";

const ID = "campus";
const W = 640;
const H = 340;
const GROUND = 252;
const c = palette.ill;

// A tree, built from a trunk and two offset canopies so it reads as foliage
// rather than a lollipop.
const Tree = ({ x, y, scale = 1, dark }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <rect x="-4" y="-34" width="8" height="38" rx="3" fill="#8a6a4a" />
    <circle cx="-11" cy="-46" r="17" fill={dark ? c.leafDeep : c.leaf} />
    <circle cx="12" cy="-49" r="15" fill={dark ? c.leaf : c.leafDeep} />
    <circle cx="0" cy="-62" r="18" fill={dark ? c.leafDeep : c.leaf} />
  </g>
);

// Two figures side by side, mid-stride.
const Walker = ({ x, y, shirt, skin, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M-5 0 L-7 20 M5 0 L8 20" stroke={c.ink} strokeWidth="5" strokeLinecap="round" />
    <path d="M-9 -22 Q-9 -34 0 -34 Q9 -34 9 -22 L9 2 L-9 2 Z" fill={shirt} />
    <circle cx="0" cy="-41" r="9" fill={skin} />
    <path d="M-1 -48 a9 9 0 0 1 10 5" stroke={c.ink} strokeWidth="6" strokeLinecap="round" fill="none" />
  </g>
);

export const Campus = ({ className }) => {
  const reduced = useReducedMotion();

  return (
    <motion.svg
      className={className}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="A small campus with trees, low buildings and people walking and talking together"
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <defs>
        <GrainDefs id={ID} />
        <clipPath id={`${ID}-panel`}>
          <rect width={W} height={H} rx="24" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${ID}-panel)`}>
        <rect width={W} height={H} fill={c.sky} />

        <motion.circle
          cx="540" cy="70" r="40" fill={c.sun}
          {...(reduced ? {} : {
            animate: { opacity: [0.85, 1, 0.85] },
            transition: { duration: 7, repeat: Infinity, ease: "easeInOut" },
          })}
        />
        <g fill="#ffffff" opacity="0.8">
          <rect x="70" y="56" width="74" height="15" rx="7.5" />
          <rect x="92" y="43" width="44" height="14" rx="7" />
          <rect x="420" y="96" width="56" height="13" rx="6.5" />
        </g>

        {/* Rolling ground */}
        <path d={`M0 ${GROUND} Q160 ${GROUND - 34} 330 ${GROUND - 6} T${W} ${GROUND - 20} L${W} ${H} L0 ${H} Z`} fill={c.leaf} opacity="0.55" />
        <rect y={GROUND + 26} width={W} height={H - GROUND - 26} fill={c.leafDeep} opacity="0.35" />

        {/* Buildings */}
        <g>
          <rect x="72" y="150" width="104" height="82" rx="6" fill={palette.paper} />
          <path d="M64 152 L124 118 L184 152 Z" fill={c.clay} />
          <rect x="88" y="172" width="18" height="18" rx="3" fill={c.skyDeep} />
          <rect x="116" y="172" width="18" height="18" rx="3" fill={c.skyDeep} />
          <rect x="144" y="172" width="18" height="18" rx="3" fill={c.skyDeep} />
          <rect x="112" y="204" width="26" height="28" rx="3" fill={c.wood} />
        </g>
        <g>
          <rect x="386" y="138" width="126" height="96" rx="6" fill={c.sand} />
          <rect x="386" y="130" width="126" height="12" rx="4" fill={c.woodDeep} />
          <rect x="404" y="158" width="20" height="20" rx="3" fill={c.skyDeep} />
          <rect x="434" y="158" width="20" height="20" rx="3" fill={c.skyDeep} />
          <rect x="464" y="158" width="20" height="20" rx="3" fill={c.skyDeep} />
          <rect x="404" y="192" width="20" height="20" rx="3" fill={c.skyDeep} />
          <rect x="434" y="192" width="20" height="20" rx="3" fill={c.skyDeep} />
          <rect x="464" y="192" width="20" height="20" rx="3" fill={c.skyDeep} />
        </g>

        {/* Path */}
        <path d={`M-20 ${H} Q200 ${GROUND + 34} 330 ${GROUND + 10} T${W + 20} ${GROUND + 2}`} stroke={c.sand} strokeWidth="34" fill="none" strokeLinecap="round" opacity="0.9" />

        <Tree x={36} y={GROUND + 6} scale={0.95} />
        <Tree x={234} y={GROUND - 2} scale={0.8} dark />
        <Tree x={356} y={GROUND + 4} scale={0.7} />
        <Tree x={600} y={GROUND + 10} scale={1} dark />

        {/* People, walking and talking */}
        <Walker x={196} y={GROUND + 52} shirt={c.plum} skin={c.skin[2]} scale={1.05} />
        <Walker x={226} y={GROUND + 52} shirt={c.river} skin={c.skin[0]} scale={1.05} />
        <Walker x={418} y={GROUND + 44} shirt={c.leafDeep} skin={c.skin[1]} scale={0.9} />
        <Walker x={470} y={GROUND + 46} shirt={c.clay} skin={c.skin[3]} scale={0.95} />

        <GrainOverlay id={ID} width={W} height={H} rx={24} />
      </g>
    </motion.svg>
  );
};

export default Campus;
